(ns minorodata.components.home-dashboard.sources-breakdown
  (:require
   ["date-fns" :refer [formatISO parseISO]]
   ["lucide-react" :refer [CheckCircleIcon CircleDashedIcon CircleHelpIcon CircleOffIcon CircleXIcon SettingsIcon]]
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.utils :as app.utils :refer [format-date]]
   [minorodata.components.home-dashboard.shared :refer [format-duration]]
   [minorodata.components.spinner.core :as spinner]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]
   [ui.accordion :as accordion]
   [ui.button :as button]
   [ui.dialog :as dialog]
   [ui.skeleton :as skeleton]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(def source-unavailable-text "Source no longer available")

(def select-card-class
  "flex flex-col gap-2 justify-between items-center p-2 border rounded transition-all hover:bg-kleene-hover-background disabled:pointer-events-none  disabled:opacity-50")

(defn visible-sources-configure-list' [{:keys [sources connectors visible-sources loading]}]
  (let [[ids set-ids] (react/useState (mapv :source/source-id visible-sources))
        toggle (fn [source-id]
                 (set-ids (if (some #(= % source-id) ids)
                            (remove #(= source-id %) ids)
                            (conj ids source-id))))]
    [:div {:class "flex flex-col gap-6 overflow-hidden"}
     [:header
      [dialog/dialog-title "Select sources to show"]
      [dialog/dialog-description "Choose up to 5 data sources to be displayed on your dashboard."]]
     [typography/p {:class (clsx "text-kleene-text-secondary text-sm"
                                 {"text-kleene-text-error" (>= (count ids) 5)})}
      (str (count ids) " / 5 selected")]
     [:div {:class "grid grid-cols-4 gap-2 overflow-y-auto flex-1"}
      (when-not loading
        (for [{:source/keys [source-id]} (filter :unavailable? visible-sources)]
          (let [checked (some #(= % source-id) ids)]
            [:button {:key source-id
                      :on-click #(toggle source-id)
                      :class (clsx select-card-class
                                   {"border-kleene-secondary-v2" checked})
                      :disabled (and (not checked) (>= (count ids) 5))}
             [:> CircleOffIcon {:class "w-14 h-14 p-3 text-kleene-text-secondary"}]
             [typography/p {:class "text-kleene-text-secondary"} source-unavailable-text]])))
      (if loading
        [:div {:class "w-full flex m-8 items-center justify-between"}
         [spinner/with-text {:text "Loading sources..."}]]

        (for [{:source/keys [source-id source-type source name]} sources]
          (let [checked (some #(= % source-id) ids)
                connector (app.utils/find-connector-by-type-and-version connectors source-type (get source :version))]
            [:button {:key source-id
                      :on-click #(toggle source-id)
                      :class (clsx select-card-class
                                   {"border-kleene-secondary-v2" checked})
                      :disabled (and (not checked) (>= (count ids) 5))}
             [:img {:src (app.utils/maybe-local-url (:image-source connector))
                    :alt name
                    :class "w-14 h-14 object-contain"}]
             [typography/h4 name]
             #_[fields/checkbox {:id source-id
                                 :label [:div {:class "flex items-center gap-1"}
                                         [:img {:src (:image-source connector)
                                                :alt name
                                                :class "w-6 h-6 object-contain"}]
                                         name]
                                 :checked checked
                                 :disabled (and (>= (count ids) 5) (not checked))
                                 :on-change #(toggle source-id)}]])))]
     [dialog/dialog-actions {:class "sticky bottom-0 bg-kleene-background"}
      [dialog/dialog-close
       [:> button/button-fr {:disabled (or loading (empty? sources))
                             :on-click #(dispatch [:analytics.events/set-source-visibility
                                                   (filterv (set (map :source/source-id sources)) ids)])}
        "Confirm"]]]]))

(defn visible-sources-configure-list []
  (let [connectors @(re-posh/subscribe [:connections.subs/all-connectors])
        panel @(re-posh/subscribe [:sources-panel/pull '[* {:sources-panel/sources [*]}]])
        visible-sources @(re-posh/subscribe [:analytics.subs/visible-sources])
        loading (or @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-sources])
                    @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-connectors]))]
    [:f> visible-sources-configure-list' {:connectors connectors
                                          :sources (:sources-panel/sources panel)
                                          :visible-sources visible-sources
                                          :loading loading}]))

(defn visible-sources-configure []
  [dialog/dialog-root
   [dialog/dialog-trigger {:as-child true}
    [:div
     [button/button {:variant :secondary
                     :icon    SettingsIcon
                     :component :div}
      "Configure"]]]
   [dialog/dialog-content {:size :large :class "flex flex-1"}
    [visible-sources-configure-list]]])

(defn source-details-content [{:keys [source]}]
  (let [timeframe @(re-posh/subscribe [:analytics.subs/timeframe])]
    (dispatch [:connections.api/fetch-extracts {:source-id (:source/source-id source)
                                                :from (formatISO (:from timeframe))
                                                :to   (formatISO (:to timeframe))
                                                :replace true}])

    (fn []
      (let [loading? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-extracts])
            extracts (some-> @(re-posh/subscribe [:extract-panel/pull '[* {:extract-panel/items [*]}]])
                             :extract-panel/items)]
        (cond
          loading?
          [:div {:class "w-full flex items-center justify-center py-8"}
           [spinner/with-text {:text "Loading extracts..."}]]

          (empty? extracts)
          [:div {:class "flex flex-col gap-4 items-center justify-center py-8"}
           [typography/p {:class "text-kleene-text-secondary"}
            "No extracts run found for selected source and period."]]

          :else
          [:div {:class "flex flex-col"}
           (for [extract extracts]
             (let [seconds (:extract/last-run-seconds extract)]
               [:div {:class "flex gap-3 items-center p-4 not-first:border-t"
                      :key (:extract/extract-id extract)}
                [tooltip/tooltip-root
                 [tooltip/tooltip-trigger
                  (case (:extract/last-run-status extract)
                    "success" [:> CheckCircleIcon {:size 24 :class "text-kleene-text-success"}]
                    "pending" [:> CircleDashedIcon {:size 24 :class "text-kleene-text-secondary"}]
                    "fail"    [:> CircleXIcon {:size 24 :class "text-kleene-text-error"}]
                    [:> CircleHelpIcon  {:size 24 :class "text-kleene-text-secondary"}])]
                 [tooltip/tooltip-content
                  (str "Last run status: " (or (:extract/last-run-status extract) "N/A"))]]
                [:div
                 [:div {:class "flex flex-col"}
                  [:a {:href (rt.easy/href :log {} {:source (:source/source-type source)
                                                    :term   (:extract/name extract)})
                       :class "text-kleene-text-link hover:underline"}
                   [typography/h4 (:extract/name extract)]]]]
                (when (:extract/last-run-at extract)
                  [:div {:class "flex-1 text-right"}
                   [typography/p {:class "text-kleene-text-secondary text-xs"}
                    (format-date (parseISO (:extract/last-run-at extract)))]
                   [typography/p {:class "text-kleene-text-secondary text-xs"}
                    (format-duration seconds)]])]))])))))

(defn source-details [{:keys [source]}]
  [accordion/accordion-item {:value (:source/source-id source)
                             :class "border-0"}
   [accordion/accordion-trigger {:class "bg-kleene-background rounded-2xl border hover:bg-kleene-hover-background radix-state-open:bg-kleene-selected-background/20 transition-colors radix-state-open:rounded-b-none px-4 py-3"
                                 :icon-right true}
    [:div {:class "flex justify-between items-center"}
     [:div {:class "flex gap-2 items-center"}
      [:div {:class "p-0.5 w-8 h-8 bg-kleene-background-border rounded-lg"}
       [:img {:src (app.utils/maybe-local-url (get-in source [:connector :image-source]))
              :alt (:source/name source)
              :class "w-full h-full object-contain"}]]
      [:div
       [typography/h4 (:source/name source)]]]]]
   [accordion/accordion-content {:class "border border-t-0 rounded-b-2xl bg-kleene-background radix-state-open:h-auto"}
    [source-details-content {:source source}]]])

(defn source-unavailable []
  [:div {:class "flex gap-2 items-center bg-kleene-background rounded-2xl border px-4 py-3"}
   [:div {:class "p-1.5 w-8 h-8 bg-kleene-background-border rounded-lg"}
    [:> CircleOffIcon {:class "w-full h-full text-kleene-text-secondary"}]]
   [typography/p {:class "text-kleene-text-secondary"} source-unavailable-text]])

(defn sources-breakdown' [{:keys [loading visible-sources]}]
  (react/useEffect
   (fn []
     (dispatch [:sources-panel/add])
     (dispatch [:extract-panel/add {:extract-panel/pagination {:items-per-page 10}}])
     (dispatch [:connections.api/fetch-user-connectors])
     (dispatch [:connections.api/fetch-sources])

     (fn []
       (dispatch-sync [:extract-panel/delete])
       (dispatch-sync [:sources-panel/delete])))
   #js [])

  [:div {:class "flex flex-col gap-4"}
   [:header {:class "flex justify-between items-center"}
    [:div
     [typography/h3 "Sources breakdown"]
     [typography/p {:class "text-kleene-text-secondary"}
      "Overview of your data sources"]]
    [:div
     [visible-sources-configure]]]
   (if loading
     [:div {:class "flex flex-col gap-2"}
      (for [i (range 5)]
        [skeleton/skeleton {:key i
                            :class "w-full rounded-lg"
                            :height 62}])]
     [accordion/accordion-root {:class "flex flex-col gap-2"
                                :collapsible true}
      (for [s visible-sources]
        (if (:unavailable? s)
          ^{:key (:source/source-id s)} [source-unavailable]
          [source-details {:key (:source/source-id s)
                           :source s}]))])])

(defn sources-breakdown []
  (let [visible-sources @(re-posh/subscribe [:analytics.subs/visible-sources])
        loading (or @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-sources])
                    @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-connectors]))]
    [:f> sources-breakdown' {:visible-sources visible-sources
                             :loading loading}]))
